import { IWorkflow } from '@augloop/interfaces';
import { SessionInitMessage, ICacheDumpRequestResponse, Message, IResponse } from '@augloop/session-protocol';
import { IAuthTokenRequest, IAuthTokenResponse, IClientMetadata } from '@augloop/schemas/dist/src/client/schemas';
import { SchemaObject, IOperation, ItemPath, AnnotationState, ISchemaObject } from '@augloop/types-core';

import { ILambda, IReduceLambda, IReduceWorkflowGroupingKey, IWorkflowRegistrationOptions } from './deprecated-runtime-types';
import { AnnotationActivationOptions, IAnnotationActivationResult, AnnotationActivationToken, CacheRequestParameters, ISession, SessionCloseCallback, SessionConnectCallback, SessionDisconnectCallback, SessionReconnectCallback } from './isession';
import { IModelDownloader } from './local-workflow-execution-manager';

/**
 * The result of multiplexing containing the combined input and a function to expand the output.
 */
export type MultiplexResult = {
    input: any;
    demultiplex: (output: any) => any[];
};

/**
 * The result of splitting the input into multiple inputs and a function to join the output.
 */
export type SplitResult = {
    inputs: any[];
    join: (outputs: any[]) => any;
};

export interface IBatchOptions {
    /**
     * Timeout interval after the last input was submitted before making a call to the service.
     */
    delayMs: number;

    /**
     * Allow at most this much input data to accumulate before making a call to the service
     * (possibly sooner than the delayMs timeout). The unit is the same as that used by the
     * estimateSize function, or bytes if no estimateSize function is provided. Individual
     * inputs larger than this threshold will still be sent (by themselves).
     */
    maxInputSize: number;
}

/**
 * Configuration for batching data of type T sent to the AL service. There are two supported
 * batching flows.
 *
 * The MULTIPLEX flow involves combining inputs and expanding outputs:
 * - multiplex:   T[]     -> TSubmit
 * - submit:      TSubmit -> USubmit
 * - demultiplex: USubmit -> U[]
 *
 * The SPLIT AND MULTIPLEX flow involves splitting and combining inputs and expanding and joining outputs:
 * - split:       T       -> T[]
 * - multiplex:   T[]     -> TSubmit
 * - submit:      TSubmit -> USubmit
 * - demultiplex: USubmit -> U[]
 * - join:        U[]     -> U
 */
export interface IBatchConfig<T> extends IBatchOptions {
    /**
     * Given an input object, produce a size estimate. This can be any unit. The return value
     * is used to produce an accumulated size estimate that is compared to maxInputSize to
     * determine if the trigger condition has been reached. If no estimateSize function is
     * provided, a built-in function that estimates the size in bytes will be used.
     */
    estimateSize: (input: T) => number;

    /**
     * Given an input object, extract a grouping key used to batch related inputs together.
     * If no groupingKeyExtractor function is provided, a built-in function will be used.
     */
    groupingKeyExtractor: (input: T) => string;

    /**
     * Given an array of input objects, multiplex (combine) them into a single input,
     * and provide a function for demultiplexing (expanding) the output into individual
     * output objects, exactly one per input object, in the same order.
     *
     * The input to multiplex is a T[] that gets combined into a TSubmit. The input to demultiplex is
     * a USubmit output that gets expanded into a U[].
     *
     * @param inputs The accumulated input objects after reaching the timeout or size threshold.
     *
     * @returns The single input object and a demultiplex callback that takes the single
     * output object and returns multiple output objects, corresponding to the inputs.
     */
    multiplex: (inputs: T[]) => MultiplexResult;

    /**
     * Given an input object, split it into an array of smaller input objects. This will be called when
     * the input given to the batch manager exceeds the maxInputSize. If no split function is provided or
     * the function returns undefined, then the input will not be split and will be sent to the AL service
     * by itself.
     *
     * The input to split is a T input that gets split into a T[]. The input to join is
     * a U[] output that gets joined into a U.
     *
     * @param input The input object added as a batch item.
     *
     * @returns An array of input objects of the same type and a join callback that takes the array of
     * output objects and joins them into a single output object, corresponding to the input.
     */
    split?: (input: T) => SplitResult | undefined;

    /**
     * Optional extractor of a single "summary" result from a given batch output.
     *
     * @param output The output object produced by a batched execution (identical to the input to
     * MultipleResult.demultiplex).
     *
     * @returns A tuple representing the summary object output schema and the summary object
     * (or [undefined, undefined] if there was nothing to extract).
     */
    summaryExtractor?: (output: any) => [string, any];
}

/**
 * Privacy properties, compatible with OTel (only the subset we may use, and without an explicit dependency on OTelJS):
 * https://office.visualstudio.com/OE/_git/OTEL-OTelJS?path=%2Foteljs%2Fsrc%2FEventFlagsProperties.ts
 */

export enum DataCategories {
    ProductServiceUsage = 2,
    ProductServicePerformance = 4
}

export enum DiagnosticLevel {
    BasicEvent = 10,
    FullEvent = 100,
    RequiredServiceDataEvent = 110
}

/**
 * Host-implemented callbacks used by the Augmentation Loop runtime.
 */
export interface IHostCallbacks {
    /**
     * Receives an augmentation result. Will be invoked multiple times for the same input if multiple
     * workflows generated outputs.
     *
     * @param inputSchema The schema of the input object originally passed to submit().
     *
     * @param input The input object originally passed to submit().
     *
     * @param outputSchema The schema of the workflow output.
     *
     * @param output The workflow output.
     */
    onResult(inputSchema: string, input: any, outputSchema: string, output: any): void;

    /**
     * Receives an augmentation result in the form of an annotation operation that adds, updates or
     * deletes an annotation in the session data model. The annotation type must be activated in order
     * to receive this notification, and it is invoked only for incremental updates after activation.
     *
     * @param operation An operation that adds, updates or deletes an annotation object.
     */
    onAnnotationResult(operation: IOperation): void;

    /**
     * Requests an authentication token.
     *
     * @param tokenRequest Authentication service parameters
     *
     * @returns Promise that resolves to an auth token.
     */
    requestAuthToken(tokenRequest: IAuthTokenRequest): Promise<IAuthTokenResponse>;

    /**
     * Allows the runtime to emit telemetry events.
     *
     * @param tenant The telemetry tenant to which this event should be routed.
     *
     * @param event The fully-qualified name of the event.
     *
     * @param data The custom data fields for this event.
     *
     * @param contract Optional fully-qualified name of the event contract to which this event conforms.
     *
     * @param contractData The data fields for the event contract, if any.
     *
     * @param critical Whether this event should bypass sampling.
     *
     * @param dataCategories OTel-compatible DataCategories of the event.
     *
     * @param diagnosticLevel OTel-compatible DiagnosticLevel of the event.
     */
    sendTelemetryEvent(tenant: string, event: string, data: object, contract: string, contractData: object, critical: boolean, dataCategories?: DataCategories, diagnosticLevel?: DiagnosticLevel): void;

    /**
     * Allows the runtime to check for enablement of flightable features.
     *
     * @param feature The fully-qualified feature name.
     *
     * @param scope The deployment scope at which this feature is on by default. Values may be 'None',
     * 'Automation', 'Dogfood', 'Internal', 'Insiders', or 'Production'. Smaller scopes are implied
     * (e.g. something flighted to 'Insiders' is also flighted to 'Internal', 'Dogfood', etc. by default
     * as well).
     *
     * @returns Promise that resolves to whether the feature is enabled or not.
     */
    isFeatureEnabled(feature: string, scope: string): Promise<boolean>;

    /**
     * The 'Native' lambda. This is a lambda that executes in the host and returns the string value of the OutputJson
     *
     * @param inputSchema The schema of the input object originally passed to submit().
     *
     * @param input The input object originally passed to submit().
     *
     * @param outputSchema The schema of the workflow output.
     *
     * @param config A lambda-specific configuration object. Multiple invocations of the lambda MAY receive the
     * same config object, giving an opportunity to cache data between calls, but persistence is not guaranteed
     * and you cannot rely on this for functional correctness.
     */
    executeLocalLambda(inputSchema: string, input: any, outputSchema: string, config?: any): Promise<any>;

    /**
     * Receives the session URL and origin from a SessionInitResponse when the session starts.
     *
     * @param sessionUrl The session URL for sending messages to AL as HTTPS requests.
     * Note also that sending request on session endpoint requires
     *  1. X-Origin header which value should be populated by origin string.
     *  2. Authorization header with string value "Bearer <JWT user token>"
     *
     * @param origin Data used for AL internal routing mechanism.
     * This is needed for requests to AL which comes to session HTTPS endpoint.
     * This value should be sent in request's header with header name "X-Origin".
     *
     * @param authToken Auth token which should be used, when sending messages to
     * AL as HTTPS requests, to claim user identity
     */
    setSessionData?(sessionUrl: string, origin: string, authToken: string): void;

    /**
     *
     * @param sessionInitMessage Default session init message that may be overridden before being sent
     * to the AL server.
     */
    overrideSessionInitMessage?(sessionInitMessage: SessionInitMessage): SessionInitMessage;

    /**
     * Checks if the current user's license allows running all of the provided license features.
     *
     * @param licenseFeatures The IDs of the license features to check.
     *
     * @returns Promise that resolves to whether all the license features are enabled or not.
     */
    areLicenseFeaturesEnabled(licenseFeatures: number[]): Promise<boolean>;

    /**
     * Allows the host to carry out logic required before establishing a session, such as downloading
     * document content asynchronously.
     *
     * @returns Promise that resolves when done with asynchronous logic.
     */
    onInitSession?(): Promise<void>;
}

/**
 * Options for client runtime initialization.
 */
export interface IRuntimeInitOptions {
    /**
     * If greater than 0, some telemetry events may be aggregated
     * using an interval timer repeating at the specified number of seconds, and flushTelemetry() must be
     * called at shutdown to force any pending telemetry to be emitted. If <= 0, aggregation is disabled.
     */
    telemetryAggregationIntervalSec: number;

    /**
     * Whether the session needs to be initialized as part of the runtime or not.
     * Sessions initialized as part of runtime support authentication using tokens.
     *
     * @deprecated Use runtime.createSession() instead.
     */
    initSession?: boolean;

    /**
     * Configuration for batching data sent to the AL service.
     */
    batchOptions?: IBatchOptions;

    /**
     * Object allowing download and local storage of workflow models.
     */
    modelDownloader?: IModelDownloader;
}

export interface ISessionCreationOptions {
    /**
     * Unique identifier used when creating multiple sessions, one for each document, within a single
     * lifetime of the runtime. Must be safe to log as EUPI, and must be supplied if creating more
     * than one session.
     */
    docSessionId?: string;

    /**
     * Optional configurations for custom session extensions. The types of the config objects
     * determine the custom session extensions that will be activated for this session.
     */
    extensionConfigs?: ISchemaObject[];

    /**
     * Optional flights to add to the client metadata for this session. Must be semicolon-separated
     * values, e.g. 'FlightA;FlightB;FlightC'.
     */
    flights?: string;

    /**
     * Session connect callback to be invoked with session parameters when the a connection is established.
     */
    onSessionConnect?: SessionConnectCallback;

    /**
     * Session disconnect callback to be invoked when a connection is closed.
     */
    onSessionDisconnect?: SessionDisconnectCallback;

    /**
     * Session disconnect callback to be invoked when a connection is closed.
     */
    onSessionReconnect?: SessionReconnectCallback;

    /**
     * Session close callback to be invoked with the session close message when the server closes the session.
     */
    onSessionClose?: SessionCloseCallback;
}

/**
 * Singleton instance of the Augmentation Loop runtime.
 */
export interface IClientRuntime {
    /**
     * Initializes the Augmentation Loop runtime.
     *
     * @param serviceUrl The root URL of the Augmentation Loop service to use for remote lambda requests.
     *
     * @param clientMetadata Client (host) metadata used for service flighting, metrics and diagnostics.
     *
     * @param hostCallbacks Host-implemented callbacks.
     *
     * @param options Options for client runtime initialization.
     *
     * @returns A promise that, once resolved, indicates that the runtime is ready to use.
     */
    init(serviceUrl: string, clientMetadata: IClientMetadata, hostCallbacks: IHostCallbacks, options: IRuntimeInitOptions): Promise<void>;

    /**
     * Loads one or more schemas from a protobuf file.
     *
     * @deprecated V1 runtime method that no longer has any effect
     *
     * @param proto The contents of a .proto file (see https://developers.google.com/protocol-buffers/docs/proto3).
     *
     * @param schemas The schemas that should be registered.
     *
     * @returns A promise that, once resolved, indicates that the schemas are ready to use.
     */
    registerSchemas(proto: string, schemas: string[]): Promise<void>;

    /**
     * Registers a workflow consisting of a single lambda that executes locally.
     *
     * @deprecated V1 runtime method that should no longer be used
     *
     * @param name Workflow name (for telemetry and debugging purposes), max 40 characters.
     *
     * @param lambda Function evaluated to examine an input object and produce an augmentation output object.
     *
     * @param options Contains all parameters related to workflow registration.
     *
     * @returns Promise that, once resolved, indicates the workflow is ready to process inputs.
     */
    registerSimpleLocalWorkflow(name: string, lambda: ILambda, options?: IWorkflowRegistrationOptions): Promise<void>;

    /**
     * Registers a workflow that executes remotely in the Augmentation Loop service.
     *
     * @deprecated V1 runtime method that should no longer be used
     *
     * @param name Workflow name (for telemetry and debugging purposes), max 40 characters.
     *
     * @param lambdaBefore Optional function evaluated to examine an input object and produce an output object
     * that matches the inputSchema of lambdaRemote (or no output, to perform filtering and abort the rest of the
     * workflow). If lambdaBefore is null, the input object will be passed directly to lambdaRemote.
     *
     * @param lambdaRemote The remote lambda to execute, defined by its inputSchema and outputSchema. If the
     * func property is null, the lambda will execute on the Augmentation Loop service; if it is non-null, you
     * can provide a custom remote call handler (e.g. perform your own HTTP request to your own service).
     *
     * @param lambdaAfter Optional function evaluated to examine the result of the lambdaRemote and it into the
     * final augmentation output object. If lambdaAfter is null, the lambdaRemote output is passed directly as
     * the final output.
     *
     * @param options Contains all parameters related to workflow registration.
     *
     * @returns Promise that, once resolved, indicates the workflow is ready to process inputs.
     */
    registerSimpleRemoteWorkflow(name: string, lambdaBefore: ILambda, lambdaRemote: ILambda, lambdaAfter: ILambda, options?: IWorkflowRegistrationOptions): Promise<void>;

    /**
     * Registers a workflow that executes lambdas based on it's type (Remote\Local\Custom).
     *
     * @deprecated V1 runtime method that should no longer be used
     *
     * @param name Workflow name (for telemetry and debugging purposes), max 40 characters.
     *
     * @param lambdas multiple lambdas of different type local, custom and remote can be defined, lambda.source is
     * a mandatory property to be defined. The lambdas are executed in squential order.
     *
     * @param options Contains all parameters related to workflow registration.
     *
     * @returns Promise that, once resolved, indicates the workflow is ready to process inputs.
     */
    registerMultipleLambdasWorkflow(name: string, lambdas: ILambda[], options?: IWorkflowRegistrationOptions): Promise<void>;

    /**
     * Registers a workflow consisting of a single lambda that reduces an array of inputs, which are outputs from other workflows.
     *
     * @deprecated V1 runtime method that should no longer be used
     *
     * @param name Workflow name (for telemetry and debugging purposes), max 40 characters.
     *
     * @param lambda Function evaluated to examine an array of input objects and produce an augmentation output object.
     * Setting inputSchema on the lambda allows this workflow to execute on outputs of workflows with a matching outputSchema.
     *
     * @param groupingKeyExtractor Funtion called on the input event and input schema to extract the grouping key.
     *
     * @param bucketSize The number of results to keep for a primary grouping key bucket.
     *
     * @param options Contains all parameters related to workflow registration.
     *
     * @returns Promise that, once resolved, indicates the workflow is ready to process inputs.
     */
    registerReduceWorkflow(name: string, lambda: IReduceLambda, groupingKeyExtractor: (input: any, inputSchema: string) => IReduceWorkflowGroupingKey, bucketSize?: number, options?: IWorkflowRegistrationOptions): Promise<void>;

    /**
     * Registers a workflow that executes lambdas based on it's type (Remote\Local\Custom).
     *
     * @deprecated V1 runtime method that should no longer be used
     *
     * @param name Workflow name (for telemetry and debugging purposes), max 40 characters.
     *
     * @param lambdas multiple lambdas of different type local, custom, and remote can be defined, lambda.source is
     * a mandatory property to be defined. The lambdas are executed in squential order. first lambda which should be reducded type
     * evaluate to examine an array of input objects and produce an augmentation output object. Setting inputSchema on the  reduce
     * lambda allows this workflow to execute on outputs of workflows with a matching outputSchema.
     *
     * @param groupingKeyExtractor Funtion called on the input event and input schema to extract the grouping key.
     *
     * @param bucketSize The number of results to keep for a primary grouping key bucket.
     *
     * @param options Contains all parameters related to workflow registration.
     *
     * @returns Promise that, once resolved, indicates the workflow is ready to process inputs.
     */
    registerMultipleLambdasReduceWorkflow(name: string, lambda: ILambda[], groupingKeyExtractor: (input: any, inputSchema: string) => IReduceWorkflowGroupingKey, bucketSize?: number, options?: IWorkflowRegistrationOptions): Promise<void>;

    /**
     * @deprecated Use createSession and call the equivalent method instead
     */
    registerLocalWorkflow(workflow: IWorkflow): void;

    /**
     * @deprecated Use createSession and call the equivalent method instead
     */
    activateAnnotation(annotationType: string, options?: AnnotationActivationOptions): Promise<IAnnotationActivationResult>;

    /**
     * @deprecated Use createSession and call the equivalent method instead
     */
    updateAnnotationConfig(token: AnnotationActivationToken, config: SchemaObject): void;

    /**
     * @deprecated Use createSession and call the equivalent method instead
     */
    releaseAnnotation(token: AnnotationActivationToken): Promise<boolean>;

    /**
     * @deprecated Use createSession and call the equivalent method instead
     */
    setAnnotationState(itemPath: ItemPath, id: string, value: AnnotationState): void;

    /**
     * Submits an input object for evaluation by all currently registered augmentation workflows. Any outputs of
     * these workflows will ultimately be returned via invocation(s) of IHostCallbacks.onResult.
     *
     * @deprecated V1 runtime method that should no longer be used
     *
     * @param inputSchema The schema of the input object.
     *
     * @param input An object conforming to inputSchema.
     *
     * @param cv An optional correlation vector (https://aka.ms/CorrelationVector) to identify this submission. If
     * provided, it must conform to the CV spec and it must be unique per IClientRuntime interface call.
     */
    submit(inputSchema: string, input: any, cv?: string): void;

    /**
     * @deprecated Use createSession and call the equivalent method instead
     */
    submitOperation(operation: IOperation, cv?: string): void;

    /**
     * @deprecated Use createSession and call the equivalent method instead
     */
    submitOperations(operations: IOperation[], cv?: string): void;

    /**
     * @deprecated Use createSession and call the equivalent method instead
     */
    submitSeedOperations(operations: IOperation[], cv?: string): void;

    /**
     * @deprecated Use createSession and call the equivalent method instead
     */
    submitCustomMessage(message: Message): Promise<IResponse>;

    /**
     * Forces any pending telemetry to be sent to IHostCallbacks.sendTelemetryEvent.
     *
     * @param stop If true, stop any further accumulation of pending telemetry and clear any interval timers.
     */
    flushTelemetry(stop: boolean): void;

    /**
     * @deprecated Use createSession and call the equivalent method instead
     */
    requestCacheDump(cacheRequestParameters?: CacheRequestParameters): Promise<ICacheDumpRequestResponse>;

    /**
     * @deprecated Use createSession and call the equivalent method instead
     */
    forceReconnectSession(): Promise<void>;

    /**
     * @deprecated Use createSession and call the equivalent method instead
     */
    setSessionCloseCallback(callback: SessionCloseCallback): void;

    /**
     * @deprecated Use createSession and call the equivalent method instead
     */
    closeSession(): void;

    /**
     * Creates a new session.
     *
     * @param options Configuration options.
     *
     * @returns Promise that resolves once the session is ready to use.
     */
    createSession(options?: ISessionCreationOptions): Promise<ISession>;
}
